import React, { useState } from 'react';

const TimeSlotModal = ({ isOpen, onClose, timeSlot, currentTime, onSave, formatTime }) => {
  const [selectedOption, setSelectedOption] = useState(currentTime.status || 'none');
  const [workText, setWorkText] = useState(currentTime.text || '');

  // Handle option selection
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    if (option === 'nothing') {
      setWorkText(''); // Clear text when "did nothing" is selected
    }
  };

  // Handle save
  const handleSave = () => {
    if (selectedOption === 'done' && !workText.trim()) {
      alert('Please describe what you did for this time slot.');
      return;
    }
    
    const status = selectedOption === 'nothing' ? 'nothing' : 
                   selectedOption === 'done' ? 'done' : 'none';
    const text = selectedOption === 'done' ? workText : '';
    
    onSave(status, text);
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset to original values
    setSelectedOption(currentTime.status || 'none');
    setWorkText(currentTime.text || '');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 modal-backdrop">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {formatTime(timeSlot)} - Update Work
          </h2>
          <p className="text-gray-600 mt-1">
            Choose what you did during this time slot
          </p>
        </div>

        {/* Options */}
        <div className="space-y-4 mb-6">
          {/* Option 1: I did nothing */}
          <button
            onClick={() => handleOptionSelect('nothing')}
            className={`w-full p-4 rounded-lg border-2 transition-all duration-200 ${
              selectedOption === 'nothing'
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedOption === 'nothing'
                  ? 'border-red-500 bg-red-500'
                  : 'border-gray-300'
              }`}>
                {selectedOption === 'nothing' && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-800">I did nothing</div>
                <div className="text-sm text-gray-500">No work completed during this time</div>
              </div>
            </div>
          </button>

          {/* Option 2: Describe what you did */}
          <button
            onClick={() => handleOptionSelect('done')}
            className={`w-full p-4 rounded-lg border-2 transition-all duration-200 ${
              selectedOption === 'done'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedOption === 'done'
                  ? 'border-green-500 bg-green-500'
                  : 'border-gray-300'
              }`}>
                {selectedOption === 'done' && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-800">I completed work</div>
                <div className="text-sm text-gray-500">Describe what you accomplished</div>
              </div>
            </div>
          </button>

          {/* Text input for work description */}
          {selectedOption === 'done' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe what you did:
              </label>
              <textarea
                value={workText}
                onChange={(e) => setWorkText(e.target.value)}
                placeholder="Enter details about your work..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                rows={4}
              />
              <div className="text-xs text-gray-500 mt-1">
                {workText.length}/500 characters
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`flex-1 px-4 py-2 rounded-lg text-white font-medium transition-colors ${
              selectedOption === 'nothing'
                ? 'bg-red-500 hover:bg-red-600'
                : selectedOption === 'done' && workText.trim()
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            disabled={selectedOption === 'done' && !workText.trim()}
          >
            {selectedOption === 'nothing' ? 'Mark as Nothing' : 'Save Work'}
          </button>
        </div>

        {/* Current Status Display */}
        {currentTime.status !== 'none' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Current status: 
              <span className={`font-medium ml-1 ${
                currentTime.status === 'nothing' ? 'text-red-600' : 'text-green-600'
              }`}>
                {currentTime.status === 'nothing' ? 'Did nothing' : 'Work completed'}
              </span>
            </div>
            {currentTime.text && (
              <div className="text-sm text-gray-600 mt-1">
                "{currentTime.text}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSlotModal;
